import { useCallback, useEffect, useState } from "react";

import Auth from "@aws-amplify/auth";

import { getCurrentAuthenticatedUser } from "~/v2/api/auth/currentUser";
import { MFAMethods } from "~/v2/hooks/useMFA";
import { MFAModal, ProfileMFA } from "./MFAComponents";

type ModalState =
  | { kind: "closed" }
  | { kind: "totp"; activating: boolean }
  | { kind: "sms" };

interface MFASettingsProps {
  mfaMethod: string | null;
  TOTPQRCode: string | null;
  TOTPSetupVerificationError: string | null;
  onRefreshMFA?: () => void;
  onDisableMFA?: () => Promise<void>;
  onSetupTOTP?: () => Promise<void>;
  onVerifyTOTPToken?: (token: string) => Promise<void>;
  showSMSOption?: boolean;
}

export const MFASettings = ({
  mfaMethod,
  TOTPQRCode,
  TOTPSetupVerificationError,
  onRefreshMFA,
  onDisableMFA,
  onSetupTOTP,
  onVerifyTOTPToken,
  showSMSOption,
}: MFASettingsProps) => {
  const [cognitoId, setCognitoId] = useState<string | undefined>(undefined);
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });

  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberately mount-only: onRefreshMFA is a caller-supplied callback and listing it would refresh MFA state in a loop.
  useEffect(() => {
    onRefreshMFA?.();
    getCurrentAuthenticatedUser().then((user) => {
      setCognitoId(user.attributes?.sub);
    });
  }, []);

  const refreshUserAttributes = useCallback(async () => {
    const user = await getCurrentAuthenticatedUser();
    user.getUserData(() => {}, { bypassCache: true });
  }, []);

  const closeModal = useCallback(() => {
    setModal({ kind: "closed" });
    if (mfaMethod !== "NOMFA") {
      window.location.reload();
    }
  }, [mfaMethod]);

  const verifyTOTPActivate = useCallback(
    async (code: string) => {
      await onVerifyTOTPToken?.(code);
      refreshUserAttributes();
    },
    [onVerifyTOTPToken, refreshUserAttributes],
  );

  const verifyTOTPDisable = useCallback(
    async (code: string) => {
      const user = await getCurrentAuthenticatedUser();
      await Auth.verifyTotpToken(user, code);
      await onDisableMFA?.();
    },
    [onDisableMFA],
  );

  const verifySMSDisable = useCallback(
    async (code: string) => {
      await Auth.verifyCurrentUserAttributeSubmit("phone_number", code);
      await onDisableMFA?.();
    },
    [onDisableMFA],
  );

  const submitPhoneNumber = useCallback(() => {
    getCurrentAuthenticatedUser().then(() => {
      Auth.verifyCurrentUserAttribute("phone_number");
    });
  }, []);

  return (
    <>
      <div>
        <div className="mb-4">
          <ProfileMFA
            type="totp"
            title="Authenticator"
            securityLevel="Secure (Required)"
            onClick={() => {
              const isActive =
                mfaMethod === MFAMethods.SOFTWARE_TOKEN_MFA.method;
              if (!isActive) {
                onSetupTOTP?.();
              }
              setModal({ kind: "totp", activating: !isActive });
            }}
            disabled={mfaMethod !== MFAMethods.SOFTWARE_TOKEN_MFA.method}
            current={mfaMethod}
          />
        </div>

        {showSMSOption && mfaMethod === MFAMethods.SMS_MFA.method && (
          <div>
            <ProfileMFA
              type="sms"
              title="SMS code"
              securityLevel="Moderately Secure"
              onClick={() => {
                Auth.verifyCurrentUserAttribute("phone_number");
                setModal({ kind: "sms" });
              }}
              disabled={false}
              current={mfaMethod}
            />
          </div>
        )}
      </div>

      {modal.kind === "totp" && (
        <MFAModal
          isOpen
          onClose={closeModal}
          activating={modal.activating}
          type="SOFTWARE_TOKEN_MFA"
          currentMethod={mfaMethod}
          onVerify={modal.activating ? verifyTOTPActivate : verifyTOTPDisable}
          cognitoId={cognitoId}
          totpQRCode={TOTPQRCode ?? undefined}
          error={TOTPSetupVerificationError ?? undefined}
        />
      )}

      {modal.kind === "sms" && (
        <MFAModal
          isOpen
          onClose={closeModal}
          activating={false}
          type="SMS_MFA"
          currentMethod={mfaMethod}
          onVerify={verifySMSDisable}
          cognitoId={cognitoId}
          onSubmitPhone={submitPhoneNumber}
        />
      )}
    </>
  );
};
